import { getSupabaseClient } from '@/integrations/supabase/client';
import { ProductIngredient } from '@/types';

function mapRow(row: {
  id: string;
  product_id: string;
  ingredient_id: string;
  quantity: number;
  organization_id: string;
  created_at: string;
}): ProductIngredient {
  return {
    id: row.id,
    productId: row.product_id,
    ingredientId: row.ingredient_id,
    quantity: Number(row.quantity),
    organizationId: row.organization_id,
    createdAt: new Date(row.created_at)
  };
}

export const productIngredientsService = {
  async getByProduct(productId: string): Promise<ProductIngredient[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_ingredients')
      .select('id, product_id, ingredient_id, quantity, organization_id, created_at')
      .eq('product_id', productId)
      .order('created_at');

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getProductIngredients(organizationId: string): Promise<ProductIngredient[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_ingredients')
      .select('id, product_id, ingredient_id, quantity, organization_id, created_at')
      .eq('organization_id', organizationId)
      .order('product_id')
      .order('created_at');

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async addIngredient(
    productId: string,
    ingredientId: string,
    quantity: number,
    organizationId: string
  ): Promise<ProductIngredient> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_ingredients')
      .insert({
        product_id: productId,
        ingredient_id: ingredientId,
        quantity: quantity,
        organization_id: organizationId
      })
      .select()
      .single();

    if (error) throw error;
    return mapRow(data);
  },

  async updateQuantity(ingredientId: string, quantity: number): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('product_ingredients')
      .update({ quantity: quantity })
      .eq('id', ingredientId);

    if (error) throw error;
  },

  async removeIngredient(ingredientId: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('product_ingredients')
      .delete()
      .eq('id', ingredientId);

    if (error) throw error;
  },

  async setRecipe(
    productId: string,
    organizationId: string,
    items: Array<{ ingredientId: string; quantity: number }>
  ): Promise<ProductIngredient[]> {
    const supabase = getSupabaseClient();

    const { error: deleteError } = await supabase
      .from('product_ingredients')
      .delete()
      .eq('product_id', productId);

    if (deleteError) throw deleteError;
    if (items.length === 0) return [];

    const rows = items.map((item) => ({
      product_id: productId,
      ingredient_id: item.ingredientId,
      quantity: item.quantity,
      organization_id: organizationId,
    }));

    const { data, error: insertError } = await supabase
      .from('product_ingredients')
      .insert(rows)
      .select('id, product_id, ingredient_id, quantity, organization_id, created_at');

    if (insertError) throw insertError;
    return (data ?? []).map(mapRow);
  },
};